import { createStore, del, get, set } from 'idb-keyval';
import { useEffect, useState } from 'react';
import { newId } from '../../doc/BoardDoc';
import { assetsUrl } from '../../app/base';
import { api } from '../../sync/api';
import { toolSettings } from '../toolSettings';
import { generateVariants, variantsFromCanvas, VARIANT_ORDER, type VariantName } from './variants';

/**
 * Asset cache and store. Originals and variants live in IndexedDB; anything missing locally is
 * fetched from the server (assets.php) and cached. New imports are uploaded by the upload queue.
 */

export interface LocalAssetMeta {
  id: string;
  mime: string;
  name: string;
  bytes: number;
  width: number;
  height: number;
  /** Variants that exist (locally or on the server), with pixel sizes. */
  variants: Partial<Record<VariantName, { w: number; h: number }>>;
  /** True once the server has it (or when the meta came from the server). */
  uploaded: boolean;
  /** 'file' for non-image attachments (no variants). */
  kind?: 'image' | 'file';
}

interface RemoteAssetMeta {
  id: string;
  boardId: string;
  mime: string;
  bytes: number;
  width: number;
  height: number;
  name: string;
  variants: Partial<Record<VariantName, { w: number; h: number; ext: string; bytes: number }>>;
}

export const assetsIdb = createStore('vds-assets', 'assets');
const metaKey = (id: string) => `meta:${id}`;
const blobKey = (id: string, v: VariantName) => `blob:${id}:${v}`;
const metaCache = new Map<string, LocalAssetMeta>();

/**
 * Imports an image for a board: generates the WebP pyramid and, unless the device settings say
 * otherwise, stores the original as lossy WebP too. The same file imported twice onto one board
 * (by content) reuses the first asset instead of storing and uploading it again.
 */
export async function storeImageAsset(file: File, boardId: string, opts: { maxSide?: number | null } = {}): Promise<LocalAssetMeta> {
  const hash = await contentHash(file);
  const existing = await findByContent(boardId, hash);
  if (existing) return existing;
  const s = toolSettings.getState();
  // format and quality are per device; whether to downscale is the board's call (0 = keep dimensions)
  const generated = await generateVariants(file, { webp: s.importWebp, quality: s.importQuality, maxSide: opts.maxSide ?? 0 });
  const id = newId();
  const original = generated.original;
  const meta: LocalAssetMeta = {
    id,
    mime: original ? 'image/webp' : file.type,
    name: original ? file.name.replace(/\.[^./]*$/, '') + '.webp' : file.name,
    bytes: original ? original.blob.size : file.size,
    width: generated.width,
    height: generated.height,
    variants: { original: { w: generated.width, h: generated.height } },
    uploaded: false,
  };
  const writes: Promise<void>[] = [set(blobKey(id, 'original'), original ? original.blob : file, assetsIdb)];
  for (const [name, v] of Object.entries(generated.variants)) {
    const variant = name as Exclude<VariantName, 'original'>;
    meta.variants[variant] = { w: v.w, h: v.h };
    writes.push(set(blobKey(id, variant), v.blob, assetsIdb));
  }
  await Promise.all(writes);
  await set(metaKey(id), meta, assetsIdb);
  metaCache.set(id, meta);
  await rememberContent(boardId, hash, id);
  return meta;
}

/**
 * Stores a painted canvas (a sketch-sheet layer) losslessly: the PNG is the original, WebP
 * variants serve zoomed-out display. Content-hashed like imports, so a layer whose pixels are
 * back to a state stored before (an undo, an untouched layer) reuses that asset.
 */
export async function storeCanvasAsset(canvas: HTMLCanvasElement, boardId: string, name = 'layer.png'): Promise<LocalAssetMeta> {
  const png = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG encoding failed'))), 'image/png'));
  const hash = await contentHash(png);
  const existing = await findByContent(boardId, hash);
  if (existing) return existing;
  const generated = await variantsFromCanvas(canvas);
  const id = newId();
  const meta: LocalAssetMeta = {
    id,
    mime: 'image/png',
    name,
    bytes: png.size,
    width: canvas.width,
    height: canvas.height,
    variants: { original: { w: canvas.width, h: canvas.height } },
    uploaded: false,
  };
  const writes: Promise<void>[] = [set(blobKey(id, 'original'), png, assetsIdb)];
  for (const [vname, v] of Object.entries(generated.variants)) {
    const variant = vname as Exclude<VariantName, 'original'>;
    meta.variants[variant] = { w: v.w, h: v.h };
    writes.push(set(blobKey(id, variant), v.blob, assetsIdb));
  }
  await Promise.all(writes);
  await set(metaKey(id), meta, assetsIdb);
  metaCache.set(id, meta);
  await rememberContent(boardId, hash, id);
  return meta;
}

/**
 * Stores a small preview render (a cutout drawer tile) as it is: lossy WebP where the browser
 * encodes it (PNG otherwise), no variants, since it is never shown larger than it was drawn.
 */
export async function storePreviewAsset(canvas: HTMLCanvasElement, boardId: string, name = 'preview'): Promise<LocalAssetMeta> {
  const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Preview encoding failed'))), 'image/webp', 0.86));
  const hash = await contentHash(blob);
  const existing = await findByContent(boardId, hash);
  if (existing) return existing;
  const id = newId();
  const meta: LocalAssetMeta = {
    id,
    mime: blob.type || 'image/png',
    name: `${name}.${blob.type === 'image/webp' ? 'webp' : 'png'}`,
    bytes: blob.size,
    width: canvas.width,
    height: canvas.height,
    variants: { original: { w: canvas.width, h: canvas.height } },
    uploaded: false,
  };
  await set(blobKey(id, 'original'), blob, assetsIdb);
  await set(metaKey(id), meta, assetsIdb);
  metaCache.set(id, meta);
  await rememberContent(boardId, hash, id);
  return meta;
}

// ---- content hashes: the same bytes imported twice onto one board reuse one asset ---------

const shaKey = (boardId: string, hash: string) => `sha:${boardId}:${hash}`;

/** SHA-256 of a file as hex, or null where WebCrypto is unavailable (plain-http LAN testing). */
async function contentHash(file: Blob): Promise<string | null> {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle) return null;
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

async function findByContent(boardId: string, hash: string | null): Promise<LocalAssetMeta | undefined> {
  if (!hash) return undefined;
  const id = await get<string>(shaKey(boardId, hash), assetsIdb);
  if (!id) return undefined;
  return getAssetMeta(id, false);
}

async function rememberContent(boardId: string, hash: string | null, id: string): Promise<void> {
  if (hash) await set(shaKey(boardId, hash), id, assetsIdb);
}

/** Stores a non-image attachment as-is (no variants); identical files on one board share an asset. */
export async function storeFileAsset(file: File, boardId: string): Promise<LocalAssetMeta> {
  const hash = await contentHash(file);
  const existing = await findByContent(boardId, hash);
  if (existing) return existing;
  const id = newId();
  const meta: LocalAssetMeta = {
    id,
    mime: file.type || 'application/octet-stream',
    name: file.name,
    bytes: file.size,
    width: 0,
    height: 0,
    variants: { original: { w: 0, h: 0 } },
    uploaded: false,
    kind: 'file',
  };
  await set(blobKey(id, 'original'), file, assetsIdb);
  await set(metaKey(id), meta, assetsIdb);
  metaCache.set(id, meta);
  await rememberContent(boardId, hash, id);
  return meta;
}

/** Copies an asset under a new id (for pasting into another board), fetching variants from the server if needed. */
export async function cloneAssetLocally(id: string): Promise<string | null> {
  const meta = await getAssetMeta(id);
  if (!meta) return null;
  const newId = newId_();
  const copy: LocalAssetMeta = { ...meta, id: newId, uploaded: false, variants: { ...meta.variants } };
  for (const v of VARIANT_ORDER) {
    if (!meta.variants[v]) continue;
    const blob = await getAssetBlob(id, v);
    if (blob) await set(blobKey(newId, v), blob, assetsIdb);
    else delete copy.variants[v];
  }
  if (!copy.variants.original) return null;
  await set(metaKey(newId), copy, assetsIdb);
  metaCache.set(newId, copy);
  return newId;
}
const newId_ = newId;

/** Opens/downloads an attachment: from the local cache, or the server when only there. */
export async function openAsset(id: string, name: string): Promise<void> {
  const blob = await getAssetBlob(id, 'original');
  if (!blob) {
    window.open(assetsUrl(id, 'original'), '_blank', 'noopener');
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export async function markUploaded(id: string): Promise<void> {
  const meta = await getAssetMeta(id);
  if (!meta || meta.uploaded) return;
  meta.uploaded = true;
  metaCache.set(id, meta);
  await set(metaKey(id), meta, assetsIdb);
}

export async function getAssetMeta(id: string, allowRemote = true): Promise<LocalAssetMeta | undefined> {
  const cached = metaCache.get(id);
  if (cached) return cached;
  const local = await get<LocalAssetMeta>(metaKey(id), assetsIdb);
  if (local) {
    metaCache.set(id, local);
    return local;
  }
  if (!allowRemote) return undefined;
  try {
    const r = await api.get<{ asset: RemoteAssetMeta }>(`/assets/${id}`);
    const variants: LocalAssetMeta['variants'] = {};
    for (const [k, v] of Object.entries(r.asset.variants)) {
      if (v) variants[k as VariantName] = { w: v.w, h: v.h };
    }
    const meta: LocalAssetMeta = {
      id: r.asset.id,
      mime: r.asset.mime,
      name: r.asset.name,
      bytes: r.asset.bytes,
      width: r.asset.width,
      height: r.asset.height,
      variants,
      uploaded: true,
    };
    metaCache.set(id, meta);
    await set(metaKey(id), meta, assetsIdb);
    return meta;
  } catch {
    return undefined;
  }
}

/** Resolves the requested variant, or the next larger one that exists. */
export async function resolveVariant(id: string, wanted: VariantName): Promise<VariantName | undefined> {
  const meta = await getAssetMeta(id);
  if (!meta) return undefined;
  const start = VARIANT_ORDER.indexOf(wanted);
  for (let i = Math.max(0, start); i < VARIANT_ORDER.length; i++) {
    const v = VARIANT_ORDER[i]!;
    if (meta.variants[v]) return v;
  }
  return undefined;
}

export async function getAssetBlob(
  id: string,
  wanted: VariantName,
  allowRemote = true,
): Promise<Blob | undefined> {
  const v = await resolveVariant(id, wanted);
  if (!v) return undefined;
  const local = await get<Blob>(blobKey(id, v), assetsIdb);
  if (local) return local;
  if (!allowRemote) return undefined;
  const meta = await getAssetMeta(id);
  if (!meta?.uploaded) return undefined;
  try {
    const res = await fetch(assetsUrl(id, v), { credentials: 'same-origin' });
    if (!res.ok) return undefined;
    const blob = await res.blob();
    await set(blobKey(id, v), blob, assetsIdb);
    return blob;
  } catch {
    return undefined;
  }
}

export async function deleteAssetLocally(id: string): Promise<void> {
  metaCache.delete(id);
  await Promise.all([del(metaKey(id), assetsIdb), ...VARIANT_ORDER.map((v) => del(blobKey(id, v), assetsIdb))]);
  for (const v of VARIANT_ORDER) urlCache.delete(`${id}:${v}`);
}

// Object URLs are cached for the page lifetime.
const urlCache = new Map<string, Promise<string | undefined>>();

export function getAssetUrl(id: string, wanted: VariantName): Promise<string | undefined> {
  const key = `${id}:${wanted}`;
  let p = urlCache.get(key);
  if (!p) {
    p = (async () => {
      const resolved = await resolveVariant(id, wanted);
      if (!resolved) return undefined;
      const resolvedKey = `${id}:${resolved}`;
      if (resolved !== wanted) {
        const existing = urlCache.get(resolvedKey);
        if (existing) return existing;
      }
      const blob = await getAssetBlob(id, resolved);
      if (!blob) return undefined;
      const url = URL.createObjectURL(blob);
      if (resolved !== wanted) urlCache.set(resolvedKey, Promise.resolve(url));
      return url;
    })();
    urlCache.set(key, p);
    // don't cache failures forever: a missing asset may arrive from the server later
    p.then((u) => {
      if (!u) urlCache.delete(key);
    });
  }
  return p;
}

/** Object URL for an asset variant once loaded; undefined while loading or missing. Retries while missing. */
export function useAssetUrl(id: string, wanted: VariantName): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let delay = 2000;
    const load = () => {
      getAssetUrl(id, wanted).then((u) => {
        if (cancelled) return;
        if (u) setUrl(u);
        else {
          timer = setTimeout(load, delay);
          delay = Math.min(delay * 2, 30000);
        }
      });
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [id, wanted]);
  return url;
}
